import { useEffect, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { AlertCircle, AlertTriangle, ChevronDown, ChevronRight, Loader2, Save } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  CATEGORY_META,
  STATUS_META,
  commitPickedQty,
  formatQty,
  formatSheetDate,
  isShortPick,
  lineLocationLabel,
  lotExpiryStatus,
  pickCategory,
} from '@/lib/pickList'
import type { PickListLine, PickZoneGroup } from '@/types/pickList'

interface Props {
  zones: PickZoneGroup[]
  collapsed: Set<string>
  onToggleZone: (zoneCode: string) => void
  /** Set when a row is selectable (phase 2 opens the picking drawer). */
  onSelectLine?: (line: PickListLine) => void
  selectedLineId?: string | null
  /**
   * Save a picked quantity, or null to clear it. Omitted on the read-only
   * paths (a completed sheet, a viewer without `pick_lists.generate`), which is
   * what makes the cell render as text rather than as a disabled input.
   */
  onPickQty?: (line: PickListLine, qtyPicked: number | null) => Promise<void>
  /** The line currently being saved, so its cell can show it is in flight. */
  savingLineId?: string | null
  /** Surfaced against the line it belongs to, not as a page-level banner. */
  pickError?: { lineId: string; message: string } | null
  /** The steps a saved pick could not be written into (z8nrz7fjzc), against its line. */
}

/**
 * The zone-grouped lines.
 *
 * Each zone is its OWN card rather than a section inside one long table: the
 * sheet is a sequence of physical trips, and one card per trip makes the route
 * legible and lets a zone collapse without leaving a gap in a shared grid.
 */
/**
 * Per-user column layout for a pick-list zone table.
 *
 * The hook is called in BOTH `PickListTable` (which renders the header) and
 * `LineRows` (which renders the cells). That is safe and deliberate: the prefs
 * live in TableColumnPrefsProvider keyed by this table id, so both components
 * read the same layout and a column hidden in the menu leaves the header and
 * every row together.
 *
 * `#` and Item are locked — the line number is how a picker calls out a row on
 * the floor, and Item is what they are fetching.
 */
const PICK_LIST_TABLE_ID = 'pick-list-lines'

const PICK_LIST_COLUMNS: TableColumnDef[] = [
  { id: 'lineNo', header: '#', locked: true, sortable: true },
  { id: 'cat', header: 'Cat', sortable: true },
  { id: 'item', header: 'Item', locked: true, sortable: true },
  { id: 'suggestedLot', header: 'Suggested lot', sortable: true },
  { id: 'expiry', header: 'Expiry', sortable: true },
  { id: 'location', header: 'Location', sortable: true },
  { id: 'onHand', header: 'On hand', sortable: true },
  { id: 'required', header: 'Required', sortable: true },
  { id: 'picked', header: 'Picked', sortable: true },
  { id: 'status', header: 'Status', sortable: true },
]

// Header sorting (z8nrz7e4h6). Lines sort WITHIN each zone card: the zones
// themselves stay in route order, since that is the walk. Unsorted, a zone's
// lines keep the sheet's own order; a line's alerts always follow it.
const PICK_LIST_SORT: SortAccessors<PickListLine> = {
  lineNo: l => l.lineNo,
  cat: l => CATEGORY_META[pickCategory(l.materialType)].label,
  item: l => l.materialCode,
  suggestedLot: l => l.suggestedLotNumber,
  expiry: l => l.suggestedExpiry,
  location: l => lineLocationLabel(l),
  onHand: l => l.onHandQty,
  required: l => l.qtyRequired,
  picked: l => l.qtyPicked,
  status: l => STATUS_META[l.status].label,
}

const PICK_LIST_HEAD_CLASSES: Record<string, string> = {
  lineNo: 'w-10 px-3 py-2 text-left font-semibold',
  cat: 'w-24 px-3 py-2 text-left font-semibold',
  item: 'px-3 py-2 text-left font-semibold',
  suggestedLot: 'w-32 px-3 py-2 text-left font-semibold',
  expiry: 'w-32 px-3 py-2 text-left font-semibold',
  location: 'w-32 px-3 py-2 text-left font-semibold',
  onHand: 'w-24 px-3 py-2 text-left font-semibold',
  required: 'w-24 px-3 py-2 text-left font-semibold',
  picked: 'w-24 px-3 py-2 text-left font-semibold',
  status: 'w-28 px-3 py-2 text-left font-semibold',
}

export function PickListTable({
  zones,
  collapsed,
  onToggleZone,
  onSelectLine,
  selectedLineId,
  onPickQty,
  savingLineId,
  pickError,
}: Props) {
  if (zones.length === 0) {
    return (
      <div className='rounded-xl border bg-card p-10 text-center text-sm text-muted-foreground'>
        No lines match the current filter.
      </div>
    )
  }

  return (
    <div className='space-y-3'>
      {zones.map(zone => (
        <ZoneCard
          key={zone.zoneCode}
          zone={zone}
          collapsed={collapsed.has(zone.zoneCode)}
          onToggle={() => onToggleZone(zone.zoneCode)}
          onSelectLine={onSelectLine}
          selectedLineId={selectedLineId}
          onPickQty={onPickQty}
          savingLineId={savingLineId}
          pickError={pickError}
        />
      ))}
    </div>
  )
}

function ZoneCard({
  zone,
  collapsed,
  onToggle,
  onSelectLine,
  selectedLineId,
  onPickQty,
  savingLineId,
  pickError,
}: {
  zone: PickZoneGroup
  collapsed: boolean
  onToggle: () => void
  onSelectLine?: (line: PickListLine) => void
  selectedLineId?: string | null
  onPickQty?: (line: PickListLine, qtyPicked: number | null) => Promise<void>
  savingLineId?: string | null
  pickError?: { lineId: string; message: string } | null
}) {
  const cols = useTableColumns(PICK_LIST_TABLE_ID, PICK_LIST_COLUMNS)
  const lines = useSortedRows(zone.lines, cols.sort, PICK_LIST_SORT)
  return (
    <section className='overflow-hidden rounded-xl border bg-card'>
      {/* Zone header — code badge, name over temperature, handling note, count. */}
      <button
        type='button'
        onClick={onToggle}
        aria-expanded={!collapsed}
        className='flex w-full items-center gap-3 bg-slate-50/80 px-4 py-2.5 text-left transition-colors hover:bg-slate-100/80'
      >
        <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#14375e] text-[11px] font-bold text-white'>
          {zone.zoneCode}
        </span>
        <span className='shrink-0'>
          <span className='block text-sm font-bold leading-tight'>{zone.zoneName}</span>
          {zone.tempRange && (
            <span className='block text-[11px] font-semibold leading-tight text-sky-700'>
              {zone.tempRange}
            </span>
          )}
        </span>
        {zone.handlingNote && (
          <span className='truncate text-xs text-muted-foreground'>{zone.handlingNote}</span>
        )}
        <span className='ml-auto flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground'>
          {zone.lines.length} line{zone.lines.length === 1 ? '' : 's'}
          {collapsed ? (
            <ChevronRight className='h-3.5 w-3.5' />
          ) : (
            <ChevronDown className='h-3.5 w-3.5' />
          )}
        </span>
      </button>

      {!collapsed && (
        <>
        {/* One toolbar per zone card, each driving the same shared layout — a
            choice made on one zone applies to every zone's table. Outside the
            scroll box below, so the button does not ride the table's width. */}
        <div className='overflow-x-persistent'>
          <table className='w-full min-w-[1000px] text-sm'>
            <thead>
              {/* On hand · Required · Picked sit together in PICK_LIST_COLUMNS:
                  they are one comparison, and reading them five columns apart is
                  what made the sheet hard to scan. */}
              <tr className='border-y text-[10px] uppercase tracking-wider text-muted-foreground'>
                {cols.visible.map(({ def }) => (
                  <ResizableTh
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={PICK_LIST_HEAD_CLASSES[def.id]}
                  >
                    {def.header}
                  </ResizableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map(line => (
                <LineRows
                  key={line.id}
                  line={line}
                  onSelect={onSelectLine}
                  selected={selectedLineId === line.id}
                  onPickQty={onPickQty}
                  saving={savingLineId === line.id}
                  error={pickError?.lineId === line.id ? pickError.message : null}
                />
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </section>
  )
}

function LineRows({
  line,
  onSelect,
  selected,
  onPickQty,
  saving,
  error,
}: {
  line: PickListLine
  onSelect?: (line: PickListLine) => void
  selected: boolean
  onPickQty?: (line: PickListLine, qtyPicked: number | null) => Promise<void>
  saving: boolean
  error: string | null
}) {
  // Same table id as the header above, so the two read one layout — see
  // PICK_LIST_TABLE_ID.
  const cols = useTableColumns(PICK_LIST_TABLE_ID, PICK_LIST_COLUMNS)
  const cat = CATEGORY_META[pickCategory(line.materialType)]
  const status = STATUS_META[line.status]
  const expiry = lotExpiryStatus(line.suggestedExpiry)
  // Info-level alerts are list-wide context, not something to shout on a row.
  const alerts = line.alerts.filter(a => a.severity !== 'info')
  const flagged = alerts.length > 0
  const clickable = !!onSelect

  return (
    <>
      <tr
        className={cn(
          'border-b last:border-0',
          clickable && 'cursor-pointer hover:bg-muted/40',
          selected && 'bg-primary/5',
          flagged && 'border-b-0',
        )}
        onClick={clickable ? () => onSelect(line) : undefined}
      >
        <ColumnCells
          columns={cols}
          cells={{
            lineNo: (
              <td
                className='px-3 py-2.5 text-muted-foreground tabular-nums'
                style={{
                  boxShadow: `inset 3px 0 0 ${flagged ? '#d97706' : cat.print.bg}`,
                }}
              >
                {line.lineNo}
              </td>
            ),
            cat: (
              <td className='px-3 py-2.5'>
                <span
                  className={cn(
                    'inline-flex rounded px-2 py-0.5 text-[10px] font-bold tracking-wide',
                    cat.cls,
                  )}
                >
                  {cat.label}
                </span>
              </td>
            ),
            item: (
              <td className='px-3 py-2.5'>
                <div className='font-semibold'>{line.materialCode}</div>
                <div className='text-xs text-muted-foreground'>{line.materialName}</div>
              </td>
            ),
            suggestedLot: <td className='px-3 py-2.5 tabular-nums'>{line.suggestedLotNumber ?? '—'}</td>,
            expiry: (
              <td className='whitespace-nowrap px-3 py-2.5'>
                <span
                  className={cn(
                    'tabular-nums',
                    expiry?.expired && 'font-bold text-red-700',
                    expiry?.nearExpiry && 'font-bold text-amber-600',
                  )}
                >
                  {formatSheetDate(line.suggestedExpiry)}
                </span>
              </td>
            ),
            location: (
              <td className='px-3 py-2.5 font-mono text-xs text-blue-700'>
                {/* Legacy bin, else the hierarchy path — reading only the legacy
                    column printed "—" for everything put away since storage landed. */}
                {lineLocationLabel(line) ?? '—'}
              </td>
            ),
            onHand: <td className='px-3 py-2.5 tabular-nums'>{formatQty(line.onHandQty, line.uom)}</td>,
            required: (
              <td className='px-3 py-2.5 font-bold tabular-nums'>
                {formatQty(line.qtyRequired, line.uom)}
              </td>
            ),
            picked: (
              <td className='px-3 py-2.5'>
                <PickedCell line={line} onSave={onPickQty} saving={saving} error={error} />
              </td>
            ),
            status: (
              <td className='px-3 py-2.5'>
                <span
                  className={cn(
                    'inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-bold tracking-wide',
                    status.cls,
                  )}
                >
                  {status.label}
                </span>
              </td>
            ),
          }}
        />
      </tr>

      {/* Alerts sit under the line they belong to, mirroring the printed sheet. */}
      {alerts.map((a, i) => (
        <tr key={i} className={cn('border-b last:border-0', selected && 'bg-primary/5')}>
          <td style={{ boxShadow: 'inset 3px 0 0 #d97706' }} />
          <td colSpan={Math.max(1, cols.visible.length - 1)} className='px-3 pb-2.5 pt-0'>
            <p
              className={cn(
                'flex items-start gap-1.5 text-xs font-medium italic',
                a.severity === 'error' ? 'text-red-700' : 'text-amber-700',
              )}
            >
              {a.severity === 'error' ? (
                <AlertCircle className='mt-0.5 h-3 w-3 shrink-0' />
              ) : (
                <AlertTriangle className='mt-0.5 h-3 w-3 shrink-0' />
              )}
              {a.message}
            </p>
          </td>
        </tr>
      ))}
    </>
  )
}

/**
 * The picked quantity, entered and then SAVED on purpose.
 *
 * Read-only unless `onSave` is supplied — a completed sheet and a viewer
 * without `pick_lists.generate` render text, not a disabled input, because a
 * disabled box invites a click that will never do anything.
 *
 * Typing only edits a draft. Nothing is recorded until the picker clicks Save
 * (or presses Enter): a pick writes into the steps it feeds and holds stock, so
 * tabbing past a box or clicking elsewhere must never record one. Escape puts
 * the saved value back. A draft not yet saved says so, and one that is not a
 * quantity says that instead of being sent.
 */
function PickedCell({
  line,
  onSave,
  saving,
  error,
}: {
  line: PickListLine
  onSave?: (line: PickListLine, qtyPicked: number | null) => Promise<void>
  saving: boolean
  /** Why the save was refused; nothing was saved (z8nrz7fn5r). */
  error: string | null
}) {
  const stored = line.qtyPicked == null ? '' : String(line.qtyPicked)
  const [draft, setDraft] = useState(stored)

  // Re-seed only when the SAVED value changes (this save landing, a revise, or
  // another picker's edit). Not when a save finishes without changing it: a
  // failed save keeps what was typed, so it can be corrected and saved again.
  useEffect(() => {
    setDraft(stored)
  }, [stored])

  if (!onSave) {
    return (
      <span className='tabular-nums text-muted-foreground'>
        {line.qtyPicked == null ? '—' : formatQty(line.qtyPicked, line.uom)}
      </span>
    )
  }

  // The rule itself lives in lib/pickList so it can be tested.
  const outcome = commitPickedQty(draft, line.qtyPicked)
  const dirty = outcome.kind === 'save'
  const invalid = outcome.kind === 'revert'
  const save = () => {
    if (outcome.kind === 'save' && !saving) void onSave(line, outcome.qtyPicked)
  }

  const short = isShortPick(line)

  return (
    <div className='space-y-0.5'>
      <div className='flex items-center gap-1.5'>
        <input
          type='number'
          min={0}
          step='any'
          value={draft}
          disabled={saving}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              save()
            }
            // Escape abandons the edit: the saved value comes back.
            if (e.key === 'Escape') setDraft(stored)
          }}
          // The row is clickable in phase 2; typing here must not also select it.
          onClick={e => e.stopPropagation()}
          aria-label={`Quantity picked for ${line.materialCode}`}
          className={cn(
            'h-8 w-20 rounded-md border bg-background px-2 text-sm tabular-nums',
            'focus:outline-none focus:ring-2 focus:ring-primary/30',
            saving && 'opacity-60',
            short && !dirty && 'border-amber-400 text-amber-700',
            dirty && 'border-primary',
            (error || invalid) && 'border-destructive',
          )}
        />
        <button
          type='button'
          onClick={e => {
            e.stopPropagation()
            save()
          }}
          disabled={!dirty || saving}
          aria-label={`Save quantity picked for ${line.materialCode}`}
          className={cn(
            'inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-medium',
            'bg-primary text-primary-foreground hover:bg-primary/90',
            'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
          )}
        >
          {saving ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Save className='h-3.5 w-3.5' />}
          Save
        </button>
      </div>
      {invalid && <p className='text-[10px] font-medium text-destructive'>Not a quantity</p>}
      {dirty && !saving && !error && <p className='text-[10px] font-medium text-primary'>Not saved yet</p>}
      {/* Short is stated, not merely coloured: colour alone is not a message. */}
      {short && !dirty && !error && (
        <p className='text-[10px] font-medium text-amber-700'>
          short of {formatQty(line.qtyRequired, line.uom)}
        </p>
      )}
      {error && <p className='max-w-56 text-[10px] font-medium text-destructive'>{error}</p>}
    </div>
  )
}
